import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Monitor, Share2, Video } from "lucide-react";
import { fetchDashboardStatsService, fetchSubscriptionUsageService } from "../../Services/dashboard.service";
import { signOut } from "../../utils/authSession";
import { EVENTS, track } from "../../utils/analytics";

const MOBILE_UA = /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i;

// Routes that must keep working on phones. The secondary camera page is opened
// on the candidate's phone by design, so blocking it would break interviews.
// The public candidate pages are reached from job links, often on a phone.
// Login and signup are where ad traffic lands; role-redirect only routes the
// signed-in user on, and the page it routes to shows the welcome screen.
// Every candidate page, from the interview's home page (testhomepage) to the
// thank-you page, is never gated: the gate is for recruiters, not candidates.
export const MOBILE_ALLOWED_PATHS = [
  "/",
  "/signup",
  "/role-redirect",
  "/secondary-camera",
  "/secondary-camera-thankyou",
  "/careers",
  "/apply",
  "/screening",
  "/overview",
  "/grievance-form",
  "/candidate",
  "/thankyou",
  "/jobinterestsurvey",
  "/candidate-login",
  "/candidate-dashboard",
  "/candidate-edit-profile",
  "/candidate-reset-password",
  "/GenieHireForm",
];

// Detected once per page load; the device does not change mid-session.
// Four checks, because each one misses something on its own:
//  - userAgentData.mobile: Chromium phones, but reports false on Android tablets
//  - UA string: Safari/Firefox phones and most tablets
//  - iPadOS 13+ claims to be a Mac, so look for touch on "MacIntel"
//  - touch-only pointer: tablets in "desktop site" mode
// Windows is always a computer: a touchscreen laptop in tablet mode, folded
// back, or with its touchpad off reports touch only (a Lenovo IdeaPad was
// turned away this way).
export const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  const nav = window.navigator;
  if (nav.userAgentData?.mobile) return true;
  if (MOBILE_UA.test(nav.userAgent)) return true;
  if (/Windows/i.test(nav.userAgent)) return false;
  if (nav.platform === "MacIntel" && nav.maxTouchPoints > 1) return true;
  const mq = (q) => window.matchMedia?.(q).matches;
  return mq("(any-pointer: coarse)") && !mq("(any-pointer: fine)");
};

// Case-insensitive, like the routes themselves
export const isMobileAllowedPath = (pathname) => {
  const path = pathname.toLowerCase();
  return MOBILE_ALLOWED_PATHS.some((p) => {
    const allowed = p.toLowerCase();
    return path === allowed || path.startsWith(`${allowed}/`);
  });
};

// Always the production address, so the link sent from a phone works on the
// computer whichever environment served this page.
const DESKTOP_URL = "https://platform.geniehire.ai/";

// The trial's allowance, or null when there is nothing to show: signed out,
// an HR login (no org-admin stats), a paid plan, or the API failing. The
// screen still works without it -- it just leaves the numbers out.
const loadTrial = async () => {
  if (!sessionStorage.getItem("accessToken")) return null;
  const stats = await fetchDashboardStatsService();
  if (!stats?.org_id) return null;
  const { subscription, usage } = await fetchSubscriptionUsageService(stats.org_id);
  if (subscription?.is_trial !== true) return null;
  const remaining = usage?.remaining || {};
  return {
    interviews: Number(remaining.interview_count_v2) || 0,
    cvs: Number(remaining.resume_screening_count) || 0,
  };
};

const TrialStat = ({ icon: Icon, value, label }) => (
  <div className="flex-1 min-w-0 flex flex-col items-center gap-1 px-2 py-4 rounded-2xl bg-[#F5F7FF] dark:bg-white/5">
    <Icon className="w-5 h-5 text-[#2960FD]" strokeWidth={2} />
    <span className="text-2xl font-bold text-[#333333] dark:text-white">{value}</span>
    <span className="text-[11px] leading-tight text-center text-[#667085] dark:text-gray-400">{label}</span>
  </div>
);

const MobileNotSupported = () => {
  const navigate = useNavigate();
  const [trial, setTrial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const signedIn = !!sessionStorage.getItem("accessToken");

  useEffect(() => {
    track(EVENTS.MOBILE_WELCOME_SHOWN, { signed_in: signedIn });
    loadTrial()
      .then(setTrial)
      .catch(() => setTrial(null))
      .finally(() => setLoading(false));
  }, [signedIn]);

  // On a phone the share sheet is the useful option -- WhatsApp or email the
  // link to yourself. Browsers without it get a plain copy.
  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "GenieHire", text: "Open GenieHire on my computer", url: DESKTOP_URL });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(DESKTOP_URL);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  // Same cleanup as the portal headers, plus the cookie: the login page
  // resumes any session it finds, so leaving it would bounce straight back here.
  const handleSignOut = async () => {
    await signOut();
    sessionStorage.removeItem("accessToken");
    navigate("/", { replace: true });
  };

  if (loading) {
    return <div className="min-h-screen bg-gradient-to-b from-[#EEF2FF] to-[#FDF2F8] dark:from-[#0B0A1F] dark:to-[#1B1524]" />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-gradient-to-b from-[#EEF2FF] to-[#FDF2F8] dark:from-[#0B0A1F] dark:to-[#1B1524]">
      <div className="w-full max-w-md bg-white dark:bg-[#1B1524] rounded-3xl shadow-sm px-6 py-8 text-center">
        {/* The real dark-theme logo, not the black one inverted: invert turns
            the pink/purple "g" and "H" green */}
        <img src="/genie-demo-logo-black.png" alt="GenieHire.ai" className="w-44 mx-auto mb-6 dark:hidden" />
        <img src="/genie-demo-logo.png" alt="GenieHire.ai" className="w-44 mx-auto mb-6 hidden dark:block" />

        {trial && (
          <span className="inline-block mb-3 px-3 py-1 rounded-full text-xs font-semibold text-[#2960FD] bg-[#EEF2FF] dark:bg-white/10 dark:text-[#AEB9E1]">
            Free trial activated
          </span>
        )}
        <h1 className="text-[26px] leading-tight font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#2960FD] to-[#FF60B6] mb-2">
          Welcome to GenieHire!
        </h1>
        <p className="text-[15px] text-[#667085] dark:text-gray-300 mb-6">
          {trial
            ? "Your free trial is ready. Here's what's waiting for you:"
            : signedIn
            ? "Your account is ready."
            : "AI-powered hiring, from job description to final interview."}
        </p>

        {trial && (
          <div className="flex gap-2 mb-6">
            <TrialStat icon={Video} value={trial.interviews} label="AI interviews" />
            <TrialStat icon={FileText} value={trial.cvs} label="CV screenings" />
          </div>
        )}

        <div className="flex items-start gap-3 text-left p-4 mb-6 rounded-2xl border border-[#E4E7EC] dark:border-white/10">
          <Monitor className="w-6 h-6 shrink-0 mt-0.5 text-[#FF60B6]" strokeWidth={2} />
          <div>
            <p className="text-[15px] font-semibold text-[#333333] dark:text-white mb-1">
              Continue on your laptop or desktop
            </p>
            <p className="text-[13px] leading-relaxed text-[#667085] dark:text-gray-400">
              Sign in at <span className="font-medium text-[#2960FD] whitespace-nowrap">{DESKTOP_URL}</span> on
              your computer using Chrome, Edge or Firefox.
            </p>
          </div>
        </div>

        <button
          onClick={shareLink}
          className="w-full py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-[#2960FD] to-[#FF60B6] flex items-center justify-center gap-2"
        >
          <Share2 className="w-4 h-4" strokeWidth={2} />
          {copied ? "Link copied" : "Send the link to myself"}
        </button>

        <p className="mt-5 text-xs text-[#98A2B3]">
          GenieHire currently supports desktop browsers only. Mobile and tablet support is coming soon.
        </p>
        {signedIn && (
          <button onClick={handleSignOut} className="mt-3 text-sm text-[#667085] hover:text-[#2960FD] dark:text-gray-400">
            Sign out
          </button>
        )}
      </div>
    </div>
  );
};

export default MobileNotSupported;
